import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { replace } from 'react-router-redux';
import qs from 'qs';
import {
  Button,
  Divider,
  Container,
  Dimmer,
  Grid,
  Header,
  Icon,
  Loader,
  Message,
  Progress,
  Popup,
  Table,
} from 'semantic-ui-react';
import moment from 'moment-timezone';
import config from '../../../config';
import { fetchM } from '../../actions/fetchM';
import { flashErrorMessage, flashSuccessMessage } from '../../actions/flash';
import { decodeSearch, encodeSearch } from '../../utils/url';
import fileSize from '../../utils/fileSize';
import isNumber from '../../utils/numbers/isNumber';
import NavBar from '../../components/NavBar';
import PageNav from '../../components/PageNav';
import Timestamp from '../../components/Timestamp';
import valueToText from '../../utils/valueToText';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import { progressToPercent, STATUSES } from '../../utils/dataImport';

moment.locale('en-gb');

class Import extends Component {
  static propTypes = {
    t: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
      query: PropTypes.object,
    }),
    appConfig: PropTypes.shape({
      data: PropTypes.shape({
        importStaffExcelExampleLink: PropTypes.string,
      }),
    }),
  }
  static mapStateToProps = state => ({
    appConfig: state.appConfig,
  })
  static contextTypes = {
    store: PropTypes.object,
  }
  static getPropsToState = (props) => {
    const query = decodeSearch(props.location.search);
    const currentPage = parseInt(query.page, 10) || 1;
    const loading = props.loading;
    return {
      currentPage,
      loading,
    };
  }
  constructor(props) {
    super(props);
    const state = Import.getPropsToState(props);
    this.state = {
      ...state,
      file: null,
      count: 0,
      data: [],
      limit: 10,
      offset: 0,
      token: null,
      updating: false,
    };
  }
  componentDidMount() {
    const query = decodeSearch(this.props.location.search);
    this.fetchStaffs();
    this.fetchImports(query);
    this.ticker = setInterval(() => {
      if (this.state.updating && this.state.currentPage === 1) {
        this.fetchImports();
      }
    }, 2000);
  }
  componentWillReceiveProps(nextProps) {
    const state = Import.getPropsToState(nextProps);

    const query = decodeSearch(nextProps.location.search);
    this.fetchStaffs();
    this.fetchImports(query);

    this.setState({ ...state });
  }
  componentWillUnmount() {
    if (this.ticker) {
      clearInterval(this.ticker);
    }
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  async fetchImports(q) {
    const { t } = this.props;
    await this.setLoading(true);
    const { store } = this.context;
    const query = Object.assign({}, q);
    if (!query.page || !isNumber(query.page) || query.page <= 0) {
      query.page = 1;
    }
    let res;
    try {
      res = await store.dispatch(fetchM(`/staff_imports/?${encodeSearch(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const count = parseInt(res.headers.get('M-Data-count'), 10);
      const limit = parseInt(res.headers.get('M-Data-limit'), 10);
      const offset = parseInt(res.headers.get('M-Data-offset'), 10);
      const data = await res.json();
      let isUpdating = this.state.updating;
      if (data && data[0]) {
        if (!isUpdating && data[0].statusID === 'in_progress') isUpdating = true;
        if (data[0].statusID === 'completed' || data[0].statusID === 'failed') isUpdating = false;
      }
      if (this.state.data && this.state.data.length < 1 && query.page > 1) {
        query.page = 1;
        store.dispatch(replace(`/users/import/?${encodeSearch(query)}`));
      }
      // handle token.. etc
      await this.setComponentState({
        count,
        data,
        limit,
        offset,
        updating: isUpdating,
      });
    }
    await this.setLoading(false);
  }
  async fetchStaffs(q) {
    const { t } = this.props;
    await this.setLoading(true);
    const { store } = this.context;
    const query = Object.assign({}, q);
    query.limit = 1;
    let res;
    try {
      res = await store.dispatch(fetchM(`${config.get('bookApiURL')}/staffs/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const token = res.headers.get('X-Csrf-Token');
      // handle token.. etc
      await this.setState({
        token,
      });
    }
    await this.setLoading(false);
  }
  submitFile(body) {
    const { t } = this.props;
    this.setLoading(true);
    this.setState({ updating: true });
    const { store } = this.context;
    store.dispatch(fetchM('/staffs/import/', {
      credentials: 'include',
      method: 'POST',
      headers: {
        // Let browser decide Content-Type
        'X-Csrf-Token': this.state.token,
      },
      body,
    })).then((res) => {
      this.setLoading(false);
      res.text().then(() => {
        this.setState({ file: null }, () => {
          this.fetchImports();
          store.dispatch(flashSuccessMessage({ header: t('Import Success', { ns: TRANSLATION_FILES.translation }), message: t('Updating staffs', { ns: TRANSLATION_FILES.translation }) }));
        });
      });
    }).catch((err) => {
      this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        err.res.text().then((message) => {
          store.dispatch(flashErrorMessage({ message }));
        });
        this.setState({ file: null, updating: false });
        return;
      }
      store.dispatch(flashErrorMessage(err));
    });
  }
  handleAddFile = (e) => {
    e.preventDefault();
    this.import_upload.click();
  }
  handleOnChange = () => {
    const files = Array.prototype.slice.call(this.import_upload.files);
    this.import_upload.value = null;
    if (window.FileReader) {
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.setState({ file });
        };
        reader.readAsDataURL(file);
      });
    }
  }
  handleOnSubmit = (e) => {
    e.preventDefault();
    this.handleSubmit();
  }
  handleSubmit = () => {
    const formData = new FormData();
    formData.append('import_upload', this.state.file);
    this.submitFile(formData);
  }
  handleDismiss = () => {
    this.setState({ file: null });
  }
  handleChangePage = (page) => {
    const query = decodeSearch(this.props.location.search);
    if (!query.page || !isNumber(query.page) || query.page <= 0) {
      query.page = 1;
    }
    const currentPage = parseInt(page || query.page, 10);
    query.page = currentPage;
    this.setState({ currentPage });
    this.replaceQuery(query);
  }
  replaceQuery(query) {
    const { store } = this.context;
    store.dispatch(replace(`/users/import/?${encodeSearch(query)}`));
  }
  render() {
    const { t } = this.props;
    return (
      <Container>
        <NavBar
          header={t('Import Users', { ns: TRANSLATION_FILES.screens })}
        />
        <Grid>
          <Grid.Row textAlign="center">
            <Grid.Column width={16}>
              <Dimmer
                inverted style={{ zIndex: 20 }}
                active={this.state.loading}
              >
                <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
              </Dimmer>
              <form
                encType="multipart/form-data"
                onSubmit={this.handleSubmit}
                ref={(ref) => { this.form = ref; }}
                style={{ display: 'none' }}
              >
                {t('Select file to upload:', { ns: TRANSLATION_FILES.translation })}
                <input
                  accept=".xlsx"
                  id="import_upload"
                  name="import_upload"
                  onChange={this.handleOnChange}
                  ref={(ref) => { this.import_upload = ref; }}
                  type="file"
                />
              </form>
              <Icon name="file excel outline" size="massive" color="grey" style={{ fontSize: '7em', opacity: '0.3' }} />
              <Header as="h2">
                {t('Bulk Import', { ns: TRANSLATION_FILES.translation })}
                <Header.Subheader>
                  {t('Import user listing using xlsx.', { ns: TRANSLATION_FILES.translation })}<br />
                  {t('Download xlsx', { ns: TRANSLATION_FILES.translation })}
                  <a
                    href={`${config.get('bookApiURL')}/staffs/export_template/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    &nbsp;{t('empty', { ns: TRANSLATION_FILES.translation })}&nbsp;
                  </a>
                  {t('template or', { ns: TRANSLATION_FILES.translation })}
                  <a
                    href={`${config.get('bookApiURL')}/staffs/export_example/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    &nbsp;{t('example', { ns: TRANSLATION_FILES.translation })}&nbsp;
                  </a>.
                </Header.Subheader>
              </Header>
              {(this.state.file && this.state.file.name) ?
                <Grid centered padded>
                  <Grid.Row style={{ padding: '0 0 0.5em' }}>
                    <Grid.Column mobile={14} tablet={9} computer={6} textAlign="left">
                      <Message
                        className="import_file_selcted"
                        info
                        onDismiss={this.handleDismiss}
                        size="tiny"
                      >
                        {this.state.file.name}
                      </Message>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              :
                <Button
                  disabled={this.state.updating}
                  onClick={this.handleAddFile}
                  style={{ marginBottom: '0.5em' }}
                >
                  {t('Browse File', { ns: TRANSLATION_FILES.translation })}
                </Button>
              }
              <Button
                disabled={!this.state.file || this.state.updating}
                onClick={this.handleOnSubmit}
                positive
                style={{ display: 'block', margin: 'auto' }}
              >
                {t('Upload', { ns: TRANSLATION_FILES.translation })}
              </Button>
              <Divider hidden />
              <Table
                striped unstackable
                selectable
              >
                <Table.Header fullWidth>
                  <Table.Row>
                    <Table.HeaderCell colSpan={3}>
                      <h3>{t('Imports History', { ns: TRANSLATION_FILES.translation })}</h3>
                    </Table.HeaderCell>
                    <Table.HeaderCell colSpan={2} textAlign="right">
                      <PageNav
                        currentPage={this.state.currentPage}
                        count={this.state.count}
                        limit={this.state.limit}
                        offset={this.state.offset}
                        dataSize={this.state.data ? this.state.data.length : 0}
                        loading={this.state.loading}
                        onChange={this.handleChangePage}
                      />
                    </Table.HeaderCell>
                  </Table.Row>
                  <Table.Row>
                    <Table.HeaderCell>{t('Name', { ns: TRANSLATION_FILES.constants })}</Table.HeaderCell>
                    <Table.HeaderCell>{t('Size', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                    <Table.HeaderCell>{t('Imported By', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                    <Table.HeaderCell>{t('Date', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
                    <Table.HeaderCell>{t('Status', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {
                    this.state.data && this.state.data.length > 0 ?
                      this.state.data.map((file, key) => (
                        <Table.Row key={key}>
                          <Table.Cell width={6}>
                            <a
                              href={`${config.get('bookApiURL')}/staff_imports/view/${file.id}/${file.name}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Icon name="file excel outline" />
                              {file.name}
                            </a>
                          </Table.Cell>
                          <Table.Cell collapsing>
                            {fileSize(file.size)}
                          </Table.Cell>
                          <Table.Cell collapsing>
                            {file.userPreferredName}
                          </Table.Cell>
                          <Table.Cell collapsing>
                            <Timestamp format="MMMM Do YYYY, h:mm a" timestamp={file.createdAt} />
                          </Table.Cell>
                          <Table.Cell width={4}>
                            <Progress
                              size="small"
                              percent={progressToPercent(file.updatedCount, file.totalCount)}
                              progress
                              color={file.statusID === 'in_progress' ? 'blue' : null}
                              success={file.statusID === 'completed'}
                              active={file.statusID === 'in progress'}
                              error={file.statusID === 'failed'}
                            >
                              {valueToText(STATUSES, file.statusID)}
                              {file.statusID === 'failed' && file.remarks && (
                                <Popup
                                  trigger={
                                    <Icon name="info circle" color="#912d2b" />
                                  }
                                  content={file.remarks}
                                  size="mini"
                                />
                              )}
                            </Progress>
                            <Timestamp format="MMMM Do YYYY, h:mm a" timestamp={file.updatedAt} />
                          </Table.Cell>
                        </Table.Row>
                      ))
                    :
                      <Table.Row>
                        <Table.Cell colSpan={5} textAlign="center">
                          {t('No imports found.', { ns: TRANSLATION_FILES.translation })}
                        </Table.Cell>
                      </Table.Row>
                  }
                </Table.Body>
              </Table>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    );
  }
}

export default withTranslation(namespaces)(connect(Import.mapStateToProps)(Import));



// WEBPACK FOOTER //
// ./src/containers/Staffs/Import.jsx